import { BasisPoints } from '@klear/basis-points'
import {
  ORDER_EVENT_STATUS_DRAFT,
  ORDER_EVENT_STATUS_ACCEPTED,
  ORDER_EVENT_STATUS_COMPLETED,
  ORDER_EVENT_STATUS_FUNDED,
  ORDER_EVENT_STATUS_VOIDED,
  ORDER_EVENT_STATUS_REJECTED
} from '@klear/constants/order-event-status'
import isNumber from 'lodash/isNumber'
import omit from 'lodash/omit'
import {
  CREDIT_CLASSIFICATION_IG,
  CREDIT_CLASSIFICATION_INSURED,
  formatInvoiceIdDisplay,
  PERMISSION_ACTION_COMPLETE,
  PERMISSION_ACTION_VOID,
  PERMISSION_ENTITY_INVOICE_OFFER
} from '@klear/constants'
import { DEFAULT_CURRENCY_CODE, type CurrencyCode } from '@klear/constants/currency'
import { missingRequiredEhid } from '@klear/credit/credit'
import { KlearDate } from '@klear/date'
import { orderEventTransition, type DocumentReadiness } from '@klear/order-event-lifecycle/lifecycle'
import type {
  FundCheck,
  FundCheckKey,
  FundCheckRemedy,
  FundCustomer,
  FundFacts,
  FundPricing,
  FundRating,
  FundReadinessDisbursement,
  FundReadinessRecovery,
  FundReadinessStatus
} from '@klear/invoice-offer/fund-readiness'
import { invoiceOfferHasValidPaymentMeta } from '@klear/invoice-offer/payment-meta'
import {
  invoiceOfferOrderEventStatusViewFromCode,
  type InvoiceOfferLifecycleState,
  type InvoiceOfferOrderEventStatusView
} from '@klear/invoice-offer/status'
import { Money } from '@klear/money'
import type { InvoiceOfferPaymentMetaWire } from '@klear/invoice-offer/invoice-offer.wire'
import type { PaymentAccountDetails } from '@klear/payment/schema'
import {
  paymentHasMoved,
  paymentIsFailed,
  paymentIsProcessing,
  paymentIsTerminalFailure,
  paymentIsVoided
} from '@klear/payment/status'

export type InvoiceOfferMeta = {
  advanceRate?: number
  autoCollectFeesOnFunding?: boolean
  paymentMeta?: InvoiceOfferPaymentMetaWire
  pricingFunderId?: number
  isAutoPriced?: boolean
  autoPriceInputHash?: string
  fxRate?: string
}

export type InvoiceOfferPricing = Omit<InvoiceOfferMeta, 'fxRate'> & {
  fxRate?: string | null
  effectiveDate?: KlearDate
  endDate?: KlearDate | null
  purchasePrice?: Money
  settlementCollection?: Money
  settlementAdvanceRecovery?: Money
  allianzCreditLimit?: Money
}

export type InvoiceOfferEventData = InvoiceOfferMeta & {
  type: string
  effectiveDate: string
  endDate?: string
  number?: string
  notes?: string
}

export type InvoiceOfferDateInput = string | KlearDate

function klearDateFromInput(value: InvoiceOfferDateInput | undefined): KlearDate | undefined {
  if (value == null) return undefined
  if (KlearDate.is(value)) return value
  return KlearDate.parse(value)
}

function partitionPromotedMeta(input: Record<string, unknown>): {
  meta: InvoiceOfferMeta
  effectiveDate?: InvoiceOfferDateInput
  endDate?: InvoiceOfferDateInput
  number?: string
  notes?: string
} {
  return {
    meta: omit(input, ['type', 'number', 'notes', 'effectiveDate', 'endDate']) as InvoiceOfferMeta,
    effectiveDate: input.effectiveDate as InvoiceOfferDateInput | undefined,
    endDate: input.endDate as InvoiceOfferDateInput | undefined,
    number: input.number as string | undefined,
    notes: input.notes as string | undefined
  }
}

export type InvoiceOfferInvoiceContext = {
  id: number
  idDisplay: string
  orderId: number
  amount: Money
  effectiveDate?: KlearDate
  endDate?: KlearDate
  customerId?: number
  customerName?: string
  customerOrganizationId?: number
  traderOrganizationId?: number
  traderId?: number
}

export type InvoiceOfferJson = {
  id?: number
  idDisplay?: string
  orderId: number
  parentId: number
  status?: string
  orderIdDisplay?: string
  effectiveDate?: KlearDate
  endDate?: KlearDate
  number?: string
  notes?: string
  amount?: Money
  settlementCollection?: Money
  settlementAdvanceRecovery?: Money
  allianzCreditLimit?: Money
  meta?: Record<string, unknown>
  customerId?: number
  customerName?: string
  customerOrganizationId?: number
  traderId?: number
  traderName?: string
  traderOrganizationId?: number
  parentIdDisplay?: string | null
  updatedAt?: string
  fundedEffectiveDate?: KlearDate
  completedEffectiveDate?: KlearDate
  fundingSpvLabel?: string
  funding?: InvoiceOfferFunding
}

export type InvoiceOfferFunding = {
  checks: FundCheck[] | null
  advancePayments: FundReadinessDisbursement[]
  recovery: FundReadinessRecovery | null
}

export const FUNDED_OFFER_STATUSES = [
  ORDER_EVENT_STATUS_FUNDED,
  ORDER_EVENT_STATUS_COMPLETED
] as const satisfies readonly InvoiceOfferLifecycleState[]

export type FundedOfferStatus = (typeof FUNDED_OFFER_STATUSES)[number]

export const COMMITTED_OFFER_STATUSES = [
  ORDER_EVENT_STATUS_ACCEPTED,
  ORDER_EVENT_STATUS_FUNDED,
  ORDER_EVENT_STATUS_COMPLETED
] as const satisfies readonly InvoiceOfferLifecycleState[]

export type CommittedOfferStatus = (typeof COMMITTED_OFFER_STATUSES)[number]

export function isCommittedOfferStatus(status: string): status is CommittedOfferStatus {
  return (COMMITTED_OFFER_STATUSES as readonly string[]).includes(status)
}

export function isFundedOfferStatus(status: string): status is FundedOfferStatus {
  return (FUNDED_OFFER_STATUSES as readonly string[]).includes(status)
}

export class InvoiceOffer {
  static readonly __klearClass = 'InvoiceOffer'

  id: number
  idDisplay: string
  readonly orderId: number
  orderIdDisplay: string
  parentId: number
  lifecycleState: InvoiceOfferLifecycleState
  status: string
  statusLabel: InvoiceOfferOrderEventStatusView['statusLabel']
  private effectiveDateBase: KlearDate
  endDate?: KlearDate
  number?: string
  notes?: string
  meta: InvoiceOfferMeta
  private amountRaw?: Money
  private settlementCollectionRaw?: Money
  private settlementAdvanceRecoveryRaw?: Money
  private allianzCreditLimitRaw?: Money
  customerId: number
  customerName: string
  customerOrganizationId: number
  traderId: number
  traderName: string
  traderOrganizationId: number
  parentIdDisplay?: string | null
  updatedAt?: string
  fundedEffectiveDate?: KlearDate
  completedEffectiveDate?: KlearDate
  fundingSpvLabel?: string
  private readonly funding: InvoiceOfferFunding | null

  constructor(props: InvoiceOfferJson) {
    const promoted = partitionPromotedMeta(props.meta ?? {})
    this.id = props.id ?? 0
    this.idDisplay = props.idDisplay ?? ''
    this.orderId = props.orderId
    this.orderIdDisplay = props.orderIdDisplay ?? ''
    this.parentId = props.parentId
    const statusView = invoiceOfferOrderEventStatusViewFromCode(props.status ?? ORDER_EVENT_STATUS_DRAFT)
    if (statusView == null) throw new Error(`Invalid invoice-offer status ${props.status}`)
    this.lifecycleState = statusView.lifecycleState
    this.status = statusView.status
    this.statusLabel = statusView.statusLabel
    this.effectiveDateBase = props.effectiveDate ?? klearDateFromInput(promoted.effectiveDate) ?? KlearDate.today()
    this.endDate = props.endDate ?? klearDateFromInput(promoted.endDate)
    this.number = props.number ?? promoted.number
    this.notes = props.notes ?? promoted.notes
    this.meta = promoted.meta
    this.amountRaw = props.amount
    this.settlementCollectionRaw = props.settlementCollection
    this.settlementAdvanceRecoveryRaw = props.settlementAdvanceRecovery
    this.allianzCreditLimitRaw = props.allianzCreditLimit
    this.customerId = props.customerId ?? 0
    this.customerName = props.customerName ?? ''
    this.customerOrganizationId = props.customerOrganizationId ?? 0
    this.traderId = props.traderId ?? 0
    this.traderName = props.traderName ?? ''
    this.traderOrganizationId = props.traderOrganizationId ?? 0
    this.parentIdDisplay = props.parentIdDisplay
    this.updatedAt = props.updatedAt
    this.fundedEffectiveDate = props.fundedEffectiveDate
    this.completedEffectiveDate = props.completedEffectiveDate
    this.fundingSpvLabel = props.fundingSpvLabel
    this.funding = props.funding ?? null
    this.assertValid()
  }

  get type(): typeof PERMISSION_ENTITY_INVOICE_OFFER {
    return PERMISSION_ENTITY_INVOICE_OFFER
  }

  get parentInvoiceIdDisplay(): string | undefined {
    if (this.parentIdDisplay) return this.parentIdDisplay
    if (this.parentId > 0) return formatInvoiceIdDisplay(this.parentId)
    return undefined
  }

  toJSON(): InvoiceOfferJson {
    return {
      id: this.id,
      idDisplay: this.idDisplay,
      orderId: this.orderId,
      orderIdDisplay: this.orderIdDisplay,
      parentId: this.parentId,
      status: this.status,
      effectiveDate: this.effectiveDateBase,
      endDate: this.endDate,
      number: this.number,
      notes: this.notes,
      amount: this.amountRaw,
      settlementCollection: this.settlementCollectionRaw,
      settlementAdvanceRecovery: this.settlementAdvanceRecoveryRaw,
      allianzCreditLimit: this.allianzCreditLimitRaw,
      meta: this.meta,
      customerId: this.customerId,
      customerName: this.customerName,
      customerOrganizationId: this.customerOrganizationId,
      traderId: this.traderId,
      traderName: this.traderName,
      traderOrganizationId: this.traderOrganizationId,
      parentIdDisplay: this.parentIdDisplay,
      updatedAt: this.updatedAt,
      fundedEffectiveDate: this.fundedEffectiveDate,
      completedEffectiveDate: this.completedEffectiveDate,
      fundingSpvLabel: this.fundingSpvLabel,
      funding: this.funding ?? undefined
    }
  }

  get isPersisted(): boolean {
    return isNumber(this.id) && this.id > 0
  }

  withoutInternalPricing(): InvoiceOffer {
    return new InvoiceOffer({
      ...this.toJSON(),
      allianzCreditLimit: undefined,
      fundingSpvLabel: undefined,
      funding: undefined,
      meta: omit(this.meta, ['pricingFunderId', 'isAutoPriced', 'autoPriceInputHash'])
    })
  }

  assertValid(): void {
    if (!(this.orderId > 0)) {
      throw new Error('Invoice offer requires an order id')
    }
  }

  get metaSnapshot(): InvoiceOfferMeta {
    return this.meta
  }

  applyPricing(pricing: InvoiceOfferPricing): void {
    if (pricing.effectiveDate != null) this.effectiveDateBase = pricing.effectiveDate
    if (pricing.endDate !== undefined) this.endDate = pricing.endDate ?? undefined
    if (pricing.purchasePrice != null) this.amountRaw = pricing.purchasePrice
    if (pricing.settlementCollection != null) this.settlementCollectionRaw = pricing.settlementCollection
    if (pricing.settlementAdvanceRecovery != null) {
      this.settlementAdvanceRecoveryRaw = pricing.settlementAdvanceRecovery
    }
    if (pricing.allianzCreditLimit != null) this.allianzCreditLimitRaw = pricing.allianzCreditLimit
    if (pricing.advanceRate != null) this.meta.advanceRate = pricing.advanceRate
    if (pricing.autoCollectFeesOnFunding != null) {
      this.meta.autoCollectFeesOnFunding = pricing.autoCollectFeesOnFunding
    }
    if (pricing.paymentMeta != null) this.meta.paymentMeta = pricing.paymentMeta
    if (pricing.pricingFunderId != null) this.meta.pricingFunderId = pricing.pricingFunderId
    if (pricing.isAutoPriced != null) this.meta.isAutoPriced = pricing.isAutoPriced
    if (pricing.autoPriceInputHash != null) this.meta.autoPriceInputHash = pricing.autoPriceInputHash
    if (pricing.fxRate === null) this.meta = omit(this.meta, 'fxRate')
    else if (pricing.fxRate != null) this.meta.fxRate = pricing.fxRate
  }

  get fxRate(): string | undefined {
    return this.meta.fxRate
  }

  get effectiveDate(): KlearDate {
    return this.effectiveDateBase
  }
  set effectiveDate(value: KlearDate) {
    this.effectiveDateBase = value
  }

  get amount(): Money {
    return this.amountRaw ?? Money.zero(this.currency)
  }

  get currency(): CurrencyCode {
    return this.amountRaw?.currency ?? this.settlementCollectionRaw?.currency ?? DEFAULT_CURRENCY_CODE
  }

  get advanceRate(): BasisPoints {
    return new BasisPoints(this.meta.advanceRate ?? 0)
  }

  get purchasePrice(): Money {
    return this.amount
  }

  get settlementCollection(): Money {
    return this.settlementCollectionRaw ?? Money.zero(this.currency)
  }

  get settlementAdvanceRecovery(): Money {
    return this.settlementAdvanceRecoveryRaw ?? Money.zero(this.currency)
  }

  get settlementNetAmount(): Money {
    return this.settlementCollection.subtract(this.settlementAdvanceRecovery)
  }

  get allianzCreditLimit(): Money | null {
    return this.allianzCreditLimitRaw ?? null
  }

  get funderId(): number | undefined {
    return this.meta.pricingFunderId
  }

  get paymentMeta(): InvoiceOfferPaymentMetaWire | undefined {
    return this.meta.paymentMeta
  }

  get klearPaymentDetails(): PaymentAccountDetails | null {
    const details = this.paymentMeta?.klearAccountDetails
    if (!details) return null
    return {
      type: details.accountType,
      id: details.accountId,
      organizationId: details.organizationId
    }
  }

  get traderPaymentDetails(): PaymentAccountDetails | null {
    const details = this.paymentMeta?.traderAccountDetails
    if (!details) return null
    return {
      type: details.accountType,
      id: details.accountId,
      organizationId: details.organizationId
    }
  }

  get autoCollectFeesOnFunding(): boolean {
    return (this.meta.autoCollectFeesOnFunding as boolean | undefined) ?? true
  }

  get isAutoPriced(): boolean {
    return this.meta.isAutoPriced === true
  }

  get autoPriceInputHash(): string | undefined {
    return this.meta.autoPriceInputHash
  }

  get isDraft(): boolean {
    return this.lifecycleState === ORDER_EVENT_STATUS_DRAFT
  }
  get isAccepted(): boolean {
    return this.lifecycleState === ORDER_EVENT_STATUS_ACCEPTED
  }
  get isFunded(): boolean {
    return isFundedOfferStatus(this.lifecycleState)
  }
  get isRejected(): boolean {
    return this.lifecycleState === ORDER_EVENT_STATUS_REJECTED
  }
  get isCompleted(): boolean {
    return this.lifecycleState === ORDER_EVENT_STATUS_COMPLETED
  }
  get isVoided(): boolean {
    return this.lifecycleState === ORDER_EVENT_STATUS_VOIDED
  }
  get isCommitted(): boolean {
    return isCommittedOfferStatus(this.lifecycleState)
  }
  get canStillBeFunded(): boolean {
    return !this.isFunded && !this.isVoided
  }
  get canReprice(): boolean {
    return this.isDraft || this.isAccepted
  }
  get canEditFees(): boolean {
    return this.canReprice || this.isFunded
  }
  get canChangeFundingControls(): boolean {
    return this.canStillBeFunded
  }
  get canVoid(): boolean {
    return orderEventTransition(PERMISSION_ENTITY_INVOICE_OFFER, this.lifecycleState, PERMISSION_ACTION_VOID) != null
  }
  get canReturnAdvance(): boolean {
    return this.lifecycleState === ORDER_EVENT_STATUS_FUNDED
  }
  get canComplete(): boolean {
    return (
      orderEventTransition(PERMISSION_ENTITY_INVOICE_OFFER, this.lifecycleState, PERMISSION_ACTION_COMPLETE) != null
    )
  }
  get canMarkCompleted(): boolean {
    return this.canComplete || this.isCompleted
  }
  get canMarkFeesPaid(): boolean {
    return this.isFunded
  }
  get priceIsEstimate(): boolean {
    return this.isAutoPriced && this.canReprice
  }
  isPastDue(asOf: KlearDate): boolean {
    return this.endDate != null && KlearDate.compare(this.endDate, asOf) < 0
  }

  get hasPaymentDetails(): boolean {
    return invoiceOfferHasValidPaymentMeta(this.paymentMeta)
  }
  get isQuote(): boolean {
    return this.isDraft || this.isRejected
  }
  get isCurrentOffer(): boolean {
    return !this.isRejected && !this.isVoided
  }

  withFunding(
    facts: FundFacts | null,
    advancePayments: readonly FundReadinessDisbursement[],
    recovery: FundReadinessRecovery | null
  ): InvoiceOffer {
    if (facts == null && this.canStillBeFunded) {
      throw new Error(`Fund facts are required for invoice offer ${this.idDisplay}, which can still be funded`)
    }
    const checks = facts == null ? null : this.checksAgainst(facts)
    return new InvoiceOffer({
      ...this.toJSON(),
      funding: { checks, advancePayments: [...advancePayments], recovery }
    })
  }

  get fundingLoaded(): boolean {
    return this.funding != null
  }

  get advancePayment(): FundReadinessDisbursement | null {
    return this.requireFunding().advancePayments[0] ?? null
  }

  get recovery(): FundReadinessRecovery | null {
    return this.requireFunding().recovery
  }

  get fundChecks(): FundCheck[] {
    const funding = this.requireFunding()
    if (funding.checks != null) return funding.checks
    if (!this.canStillBeFunded) return []
    throw new Error(`Fund checks were not loaded for invoice offer ${this.idDisplay}`)
  }

  get fundBlockers(): FundCheck[] {
    return this.fundChecks.filter((check) => check.reason != null)
  }

  fundBlockersAgainst(lockedFacts: FundFacts): FundCheck[] {
    return this.checksAgainst(lockedFacts).filter((check) => check.reason != null)
  }

  get fundReadinessStatus(): FundReadinessStatus {
    const { recovery } = this.requireFunding()
    const advancePayment = this.advancePayment
    if (this.isCompleted) return 'completed'
    if (this.isVoided) return 'voided'
    if (this.isFunded) {
      if (recovery?.inTransit === true && new Money(recovery.remaining).isZero()) return 'repaying'
      return recovery?.overdue === true ? 'overdue' : 'funded'
    }
    if (advancePayment != null) {
      return paymentIsTerminalFailure(advancePayment.status) ? 'fundingFailed' : 'funding'
    }
    if (this.isQuote) return 'quote'
    return this.fundBlockers.length === 0 ? 'ready' : 'blocked'
  }

  get readyToFund(): boolean {
    return this.fundingLoaded && this.fundReadinessStatus === 'ready'
  }

  get blockedFromFunding(): boolean {
    if (!this.fundingLoaded || !this.canStillBeFunded || this.advancePayment != null) return false
    return this.fundBlockers.length > 0
  }

  get readyToFundBlocker(): string | null {
    if (this.readyToFund) return null
    if (!this.fundingLoaded) return 'Fund checks were not loaded'
    if (this.isFunded) return 'Offer is already funded'
    if (this.isVoided) return 'Offer is voided'
    const payment = this.advanceToAct
    if (payment != null) {
      return `advance payment ${this.paymentLabel(payment)} already exists (${payment.status}). ${this.advancePaymentNextStep}`
    }
    return this.fundBlockers[0]?.reason ?? 'Offer is not ready to fund'
  }

  get advancePaymentNextStep(): string | null {
    const payment = this.fundingLoaded && !this.isFunded ? this.advanceToAct : undefined
    if (payment == null) return null
    if (advanceMayStillSend(payment)) {
      return paymentIsFailed(payment.status)
        ? 'Retry or delete it in Disbursements.'
        : 'The offer is marked Funded once it goes out.'
    }
    if (advanceWentOut(payment)) return 'It went out, but the offer was not marked funded. Mark it funded by hand.'
    return 'It cannot be sent again. Pay the advance another way, then mark the offer funded by hand.'
  }

  get canMarkFunded(): boolean {
    if (this.isFunded) return true
    if (!this.fundingLoaded || !this.isAccepted || this.advanceMayStillSend) return false
    return this.markFundedBlockers.length === 0
  }

  get markFundedBlocker(): string | null {
    if (this.canMarkFunded) return null
    if (this.isVoided) return 'Offer is voided'
    if (!this.fundingLoaded) return 'Fund checks were not loaded'
    const pending = this.pendingAdvance
    if (pending != null) {
      return `advance payment ${this.paymentLabel(pending)} can still be sent (${pending.status}). ${pendingAdvanceStep(pending)} instead of marking the offer funded by hand`
    }
    return this.markFundedBlockers[0]?.reason ?? 'Offer is not ready to fund'
  }

  private get markFundedBlockers(): FundCheck[] {
    const priceLocked = this.requireFunding().advancePayments.some(advanceWentOut)
    return this.fundBlockers.filter((check) => !(priceLocked && check.key === 'pricing'))
  }

  private get advanceMayStillSend(): boolean {
    return this.pendingAdvance != null
  }

  private get pendingAdvance(): FundReadinessDisbursement | undefined {
    return this.requireFunding().advancePayments.find(advanceMayStillSend)
  }

  private get advanceToAct(): FundReadinessDisbursement | undefined {
    return this.pendingAdvance ?? this.advancePayment ?? undefined
  }

  private paymentLabel(payment: FundReadinessDisbursement): string {
    return payment.paymentIdDisplay ?? `${payment.paymentId}`
  }

  private requireFunding(): InvoiceOfferFunding {
    if (this.funding == null) throw new Error(`Funding was not loaded for invoice offer ${this.idDisplay}`)
    return this.funding
  }

  private checksAgainst(facts: FundFacts): FundCheck[] {
    return this.gateChecks(facts).map((check) => {
      const remedy = check.reason == null ? null : this.remedyFor(check.key, facts)
      return remedy == null ? check : { ...check, remedy }
    })
  }

  private remedyFor(key: FundCheckKey, facts: FundFacts): FundCheckRemedy | null {
    if (key === 'platformFee' && this.canReprice) return 'applyDefaultPlatformFee'
    if (key === 'paymentDetails' && this.canReprice) return 'applyDefaultPaymentDetails'
    const isCreditCheck =
      key === 'ehid' || key === 'creditHeadroom' || (key === 'pricing' && creditRefused(facts.pricing))
    if (isCreditCheck && this.canStillBeFunded && facts.customer != null) return 'editCustomerCredit'
    return null
  }

  private gateChecks(facts: FundFacts): FundCheck[] {
    return [
      { key: 'accepted', reason: this.acceptanceBlocker },
      documentCheck('invoice', 'Invoice', facts.invoice),
      documentCheck('purchaseOrder', 'PO', facts.purchaseOrder),
      documentCheck('proofOfAcceptance', 'POA', facts.proofOfAcceptance),
      {
        key: 'purchasePrice',
        reason: this.purchasePrice.isPositive() ? null : 'Purchase price is zero, so there is no advance to send'
      },
      {
        key: 'paymentDetails',
        reason: this.hasPaymentDetails
          ? null
          : 'Payment details missing: payment network, trader account or funder settlement account'
      },
      { key: 'platformFee', reason: facts.hasPlatformFee ? null : 'No platform fee on this offer' },
      pricingCheck(facts.pricing),
      ...creditChecks(this.purchasePrice, facts.customer)
    ]
  }

  private get acceptanceBlocker(): string | null {
    if (this.isCommitted) return null
    if (this.isVoided) return 'Offer is voided'
    return this.isRejected ? 'Offer was rejected' : 'Not accepted yet'
  }
}

function advanceMayStillSend(payment: FundReadinessDisbursement): boolean {
  return !payment.sentToProvider && !paymentHasMoved(payment.status) && !paymentIsVoided(payment.status)
}

function advanceWentOut(payment: FundReadinessDisbursement): boolean {
  return paymentHasMoved(payment.status) || (payment.sentToProvider && !paymentIsTerminalFailure(payment.status))
}

function pendingAdvanceStep(payment: FundReadinessDisbursement): string {
  if (paymentIsFailed(payment.status)) return 'Retry or delete it in Disbursements'
  if (paymentIsProcessing(payment.status)) return 'Wait for it to send'
  return 'Let it send, or delete it in Disbursements'
}

function documentCheck(
  key: 'invoice' | 'purchaseOrder' | 'proofOfAcceptance',
  name: string,
  document: FundFacts['invoice'] & { isPlaceholder?: boolean }
): FundCheck {
  const eventId = document.event?.id ?? null
  if (document.isPlaceholder === true) {
    return { key, eventId, reason: `${name} is a $0 placeholder. Waiting on the trader` }
  }
  const reasons: Record<DocumentReadiness, string | null> = {
    approved: null,
    missing: `${name} missing. Waiting on the trader`,
    awaitingApproval: `${name} awaiting approval`,
    waitingOnTrader: `${name} ${document.event?.status.toLowerCase() ?? 'not approved'}. Waiting on the trader`
  }
  return { key, eventId, reason: reasons[document.status] }
}

function ratingLabel({ rating, agency }: FundRating): string {
  return `${agency} ${rating}`
}

function creditRefused(pricing: FundPricing | null): boolean {
  return pricing?.refusal != null
}

function pricingCheck(pricing: FundPricing | null): FundCheck {
  if (pricing == null) return { key: 'pricing', reason: null, outcome: 'notNeeded' }
  const { pricedAt, pricesAt, refusal, funder } = pricing
  if (refusal != null || pricesAt == null) {
    return { key: 'pricing', reason: refusal ?? 'Customer credit cannot be priced' }
  }
  if (funder != null && !funder.takesClassification) {
    return { key: 'pricing', reason: `${funder.name} does not fund ${pricesAt.classification} customers` }
  }
  const stale = pricedAt.find(({ rating }) => rating !== pricesAt.rating)
  if (stale != null) {
    return {
      key: 'pricing',
      reason: `Priced at ${ratingLabel(stale)}, but the customer now prices at ${ratingLabel(pricesAt)}`
    }
  }
  return { key: 'pricing', reason: null, outcome: pricedAt.length === 0 ? 'notCompared' : 'passed' }
}

function creditChecks(price: Money, customer: FundCustomer | null): FundCheck[] {
  if (customer == null) {
    return [
      { key: 'ehid', reason: null, outcome: 'notNeeded' },
      { key: 'creditHeadroom', reason: null, outcome: 'notNeeded' }
    ]
  }
  const { credit } = customer
  const ehid: FundCheck = missingRequiredEhid(credit?.creditClassification, customer.allianzEhid)
    ? { key: 'ehid', reason: 'Customer is not investment grade and has no EHID' }
    : {
        key: 'ehid',
        reason: null,
        outcome: credit?.creditClassification === CREDIT_CLASSIFICATION_IG ? 'notNeeded' : 'passed'
      }
  const available = credit?.availableCreditLimit
  if (credit?.creditClassification !== CREDIT_CLASSIFICATION_INSURED || available == null) {
    return [ehid, { key: 'creditHeadroom', reason: null, outcome: 'notNeeded' }]
  }
  if (price.currency !== available.currency) {
    return [ehid, { key: 'creditHeadroom', reason: null, outcome: 'notCompared' }]
  }
  if (price.lessThanOrEqualTo(available)) return [ehid, { key: 'creditHeadroom', reason: null, outcome: 'passed' }]
  return [
    ehid,
    {
      key: 'creditHeadroom',
      reason: `${customer.name ?? 'The customer'} has ${available.toString()} of Allianz credit available. Offer needs ${price.toString()}`
    }
  ]
}

export function invoiceOfferToEventData(offer: InvoiceOffer): InvoiceOfferEventData {
  const data: InvoiceOfferEventData = {
    ...offer.metaSnapshot,
    type: PERMISSION_ENTITY_INVOICE_OFFER,
    effectiveDate: offer.effectiveDate.toJSON(),
    endDate: offer.endDate?.toJSON()
  }
  if (offer.number != null) data.number = offer.number
  if (offer.notes != null) data.notes = offer.notes
  return data
}
